import React from "react";
import { Icon } from "../core/Icon.jsx";

/* Checkbox — square, green when checked. */
export function Checkbox({ checked = false, onChange, label, disabled = false, indeterminate = false, style, ...rest }) {
  const on = checked || indeterminate;
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontFamily: "var(--font-sans)",
        fontWeight: 500,
        fontSize: 14,
        color: "var(--text-body)",
        ...style,
        // Layout is structural, not cosmetic — never let caller `style` collapse the box/label gap.
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
      }}
      {...rest}
    >
      {/* Relative wrapper gives the hidden input a real hit-box (inset: 0) so Playwright pointer events land. */}
      <span style={{ position: "relative", width: 20, height: 20, flex: "none" }}>
        <input
          ref={inputRef}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: disabled ? "not-allowed" : "pointer",
            margin: 0,
            zIndex: 1,
          }}
        />
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 5,
            background: on ? "var(--green-bright)" : "var(--white)",
            boxShadow: on ? "none" : "inset 0 0 0 1.5px var(--border-strong)",
            transition: "background var(--dur) var(--ease)",
          }}
        >
          {indeterminate ? (
            <span style={{ width: 10, height: 2, borderRadius: 1, background: "var(--slate)" }} />
          ) : checked ? (
            <Icon name="check" size={14} color="var(--slate)" strokeWidth={3} />
          ) : null}
        </span>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
}

export function CheckboxGroup({ value = [], onChange, options = [], gap = 12, direction = "column", style, ...rest }) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const toggle = (optionValue) => {
    if (!onChange) return;
    onChange(value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue]);
  };
  return (
    <div style={{ display: "flex", flexDirection: direction, gap, ...style }} {...rest}>
      {norm.map((o) => (
        <Checkbox
          key={o.value}
          label={o.label}
          checked={value.includes(o.value)}
          onChange={() => toggle(o.value)}
          disabled={o.disabled}
        />
      ))}
    </div>
  );
}

export default Checkbox;
